/* Optional multi-product set builder. Prices and stock are ultimately checked by Shopify. */
(() => {
  'use strict';
  if (customElements.get('divine-pdp-bundle')) return;

  class DivinePdpBundle extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.primaryData = JSON.parse(this.querySelector('[data-primary-data]').textContent);
      this.rows = [...this.querySelectorAll('[data-piece]')].map((element) => ({
        element,
        data: JSON.parse(element.querySelector('[data-piece-data]').textContent),
        include: element.querySelector('[data-include]'),
        select: element.querySelector('[data-variant]'),
        quantity: element.querySelector('[data-piece-quantity]'),
      }));
      this.button = this.querySelector('[data-add-set]');
      this.defaultButtonLabel = this.querySelector('[data-button-label]').textContent;
      this.productInfo = this.closest('product-info') || document;
      const options = { signal: this.controller.signal };
      this.addEventListener('change', (event) => {
        const row = this.rows.find((entry) => entry.element.contains(event.target));
        if (row && event.target === row.select) this.updateRow(row, true);
        this.refresh();
      }, options);
      this.addEventListener('input', () => this.refresh(), options);
      this.productInfo.addEventListener('change', (event) => {
        if (!this.contains(event.target)) this.refresh();
      }, options);
      this.productInfo.addEventListener('input', (event) => {
        if (!this.contains(event.target)) this.refresh();
      }, options);
      this.button.addEventListener('click', () => this.addSet(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;
          if (data.variant) {
            const variant = this.primaryData.variants.find((entry) => String(entry.id) === String(data.variant.id));
            if (variant) Object.assign(variant, { available: data.variant.available, price: data.variant.price });
          }
          this.refresh();
        });
      }
      const selected = this.getPrimary().variant;
      this.rows.forEach((row) => {
        if (row.element.dataset.pair === 'true' && selected?.available && row.data.variants.some((variant) => variant.id === selected.id)) {
          row.select.value = String(selected.id);
        }
        this.updateRow(row, true);
      });
      this.classList.add('divine-set--ready');
      this.refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.unsubscribe?.();
      this.unsubscribe = null;
    }

    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    }

    getPrimary() {
      const form = document.getElementById(this.dataset.formId);
      const id = form?.querySelector('[name="id"]')?.value;
      const variant = this.primaryData?.variants.find((entry) => String(entry.id) === String(id));
      const quantityInput = form?.querySelector('[name="quantity"]') ||
        this.productInfo.querySelector('[name="quantity"][form="' + this.dataset.formId + '"]');
      const quantity = Number(quantityInput?.value || variant?.min || 1);
      return { form, variant, quantity };
    }

    updateRow(row, resetQuantity = false) {
      const variant = row.data.variants.find((entry) => String(entry.id) === row.select.value);
      if (!variant) return;
      row.element.querySelector('[data-piece-price]').textContent = this.money(variant.price);
      const image = row.element.querySelector('.divine-set__photo img');
      if (image && variant.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
      }
      row.quantity.min = variant.min || 1;
      row.quantity.step = variant.increment || 1;
      if (variant.max != null) row.quantity.max = variant.max;
      else row.quantity.removeAttribute('max');
      if (resetQuantity) row.quantity.value = variant.min || 1;
      row.element.querySelector('[data-piece-link]').href = row.data.url + '?variant=' + variant.id;
    }

    quantityError(variant, quantity, title) {
      const min = Number(variant.min) || 1;
      const step = Number(variant.increment) || 1;
      if (!Number.isInteger(quantity) || quantity < min || (quantity - min) % step !== 0) {
        return `${title}: choose a quantity of at least ${min}, in increments of ${step}.`;
      }
      if (variant.max != null && quantity > Number(variant.max)) return `${title}: the maximum quantity is ${variant.max}.`;
      return '';
    }

    selection() {
      const primary = this.getPrimary();
      if (!primary.form || !primary.variant?.available) return { error: 'Choose an available finish above to create your set.' };
      if (primary.form.querySelector('[name="selling_plan"]')?.value) return { error: 'Please use the main purchase button for subscription purchases.' };
      if (this.primaryData.variants.length > 250) return { error: 'Please add your pieces individually using their product pages.' };
      const items = [{ id: primary.variant.id, quantity: primary.quantity, variant: primary.variant, title: this.primaryData.title }];
      for (const row of this.rows) {
        if (!row.include.checked) continue;
        const variant = row.data.variants.find((entry) => String(entry.id) === row.select.value);
        if (!variant?.available) return { error: 'One of your selected finishes is unavailable. Please choose another.' };
        items.push({ id: variant.id, quantity: Number(row.quantity.value), variant, title: row.data.title });
      }
      if (items.length === 1) return { error: 'Choose another piece to create your set.', empty: true };
      for (const item of items) {
        const error = this.quantityError(item.variant, item.quantity, item.title);
        if (error) return { error };
      }
      return { items, count: items.reduce((total, item) => total + item.quantity, 0), total: items.reduce((total, item) => total + item.variant.price * item.quantity, 0) };
    }

    refresh() {
      if (!this.rows) return;
      const primary = this.getPrimary();
      this.querySelector('[data-primary-price]').textContent = primary.variant ? this.money(primary.variant.price * primary.quantity) : '—';
      this.querySelector('[data-primary-title]').textContent = primary.variant?.title === 'Default Title' ? this.primaryData.title : (primary.variant?.title || 'Choose a finish above');
      this.querySelector('[data-primary-quantity]').textContent = primary.quantity > 1 ? '× ' + primary.quantity : '';
      this.rows.forEach((row) => {
        const active = row.include.checked;
        row.element.classList.toggle('divine-set__piece--selected', active);
        row.element.querySelector('[data-options]').hidden = !active;
        row.include.disabled = !!this.busy || !row.data.variants.some((variant) => variant.available);
        row.select.disabled = !active || !!this.busy;
        row.quantity.disabled = !active || !!this.busy;
      });
      const selection = this.selection();
      this.button.disabled = !!selection.error || !!this.busy;
      this.querySelector('[data-summary]').hidden = !!selection.error;
      const guidance = this.querySelector('[data-guidance]');
      guidance.textContent = selection.error || 'Includes your selected piece above and the additions you chose.';
      if (!selection.error) {
        this.querySelector('[data-total]').textContent = this.money(selection.total);
        this.querySelector('[data-piece-count]').textContent = selection.count;
      }
    }

    error(message) {
      const element = this.querySelector('[data-error]');
      element.textContent = message || '';
      element.hidden = !message;
      this.querySelector('[data-error-cart]').hidden = !message;
    }

    requestItems(selection) {
      const primary = this.getPrimary();
      const properties = {};
      for (const [key, value] of new FormData(primary.form)) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
        if (match && typeof value !== 'string' && value.size) throw new Error('Please add personalized uploads using the main Add to cart button.');
      }
      const merged = new Map();
      selection.items.forEach((item, index) => {
        const itemProperties = index === 0 ? properties : {};
        const key = String(item.id) + JSON.stringify(itemProperties);
        if (merged.has(key)) merged.get(key).quantity += item.quantity;
        else merged.set(key, { id: item.id, quantity: item.quantity, ...(Object.keys(itemProperties).length ? { properties: itemProperties } : {}) });
      });
      for (const item of merged.values()) {
        const source = selection.items.find((entry) => entry.id === item.id);
        const error = this.quantityError(source.variant, item.quantity, source.title);
        if (error) throw new Error(error);
      }
      return [...merged.values()];
    }

    async addSet() {
      if (this.busy) return;
      const selection = this.selection();
      this.error();
      if (selection.error) { this.error(selection.error); return; }
      const { form } = this.getPrimary();
      if (form.reportValidity && !form.reportValidity()) return;
      let items;
      try { items = this.requestItems(selection); }
      catch (error) { this.error(error.message); return; }
      this.busy = true;
      this.refresh();
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-button-label]').textContent = 'Adding your pieces…';
      this.querySelector('.divine-set__spinner').hidden = false;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const cartUrl = this.dataset.cartUrl || '/cart';
      const sections = cart?.getSectionsToRender().map((section) => section.id) || [];
      let added = false;
      try {
        // Quantity limits also apply to pieces that are already in the cart.
        const cartResponse = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('We could not check your cart. Please try again.');
        const currentCart = await cartResponse.json();
        for (const item of items) {
          const source = selection.items.find((entry) => entry.id === item.id);
          const existingQuantity = currentCart.items.filter((line) => String(line.variant_id) === String(item.id)).reduce((total, line) => total + line.quantity, 0);
          const selectedQuantity = items.filter((line) => String(line.id) === String(item.id)).reduce((total, line) => total + line.quantity, 0);
          if (source.variant.max != null && existingQuantity + selectedQuantity > Number(source.variant.max)) {
            throw new Error(`${source.title}: the maximum is ${source.variant.max}, including pieces already in your cart.`);
          }
        }
        cart?.setActiveElement(this.button);
        const response = await fetch(this.dataset.addUrl || '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, ...(sections.length ? { sections, sections_url: window.location.pathname } : {}) }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error((typeof result.description === 'string' && result.description) || 'One of these pieces could not be added. Check availability and your cart before trying again.');
        }
        added = true;
        this.querySelector('[data-status]').textContent = 'Your selected pieces were added to your cart.';
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'divine-pdp-bundle', productVariantId: items[0].id, cartData: result })).catch(() => {});
        }
        if (!cart || !result.sections || sections.some((id) => !result.sections[id])) {
          window.location.assign(cartUrl);
          return;
        }
        // Dawn notifications expect a single item key; select the main item from the multi-add response.
        const firstItem = result.items?.[0] || result;
        cart.classList.remove('is-empty');
        cart.renderContents({ ...result, id: firstItem.id, key: firstItem.key });
      } catch (error) {
        if (added) {
          // The purchase action succeeded. Never retry a successful add after a presentation failure.
          window.location.assign(cartUrl);
        } else {
          this.error(error.name === 'TypeError' || error.name === 'SyntaxError' ? 'We could not confirm your pieces were added. Please check your cart before trying again.' : (error.message || 'We could not add your pieces. Please check your cart before trying again.'));
        }
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.querySelector('[data-button-label]').textContent = this.defaultButtonLabel;
        this.querySelector('.divine-set__spinner').hidden = true;
        this.refresh();
      }
    }
  }
  customElements.define('divine-pdp-bundle', DivinePdpBundle);
})();
